import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, AuthError, authInputClass, authButtonClass } from "@/components/auth/AuthShell";

const MIN_LEN = 8;

function useTokenCheck(token) {
  const [state, setState] = useState(token ? "checking" : "invalid");
  useEffect(() => {
    if (!token) return;
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`)
      .then(() => setState("valid"))
      .catch(() => setState("invalid"));
  }, [token]);
  return state;
}

function InvalidLink() {
  return (
    <div className="space-y-4" data-testid="reset-invalid">
      <AuthError testid="reset-invalid-msg">This reset link is invalid or has expired.</AuthError>
      <Link to="/forgot-password" data-testid="request-new-link" className="text-sm text-amber hover:underline">
        Request a new link →
      </Link>
    </div>
  );
}

function ResetDone() {
  return (
    <div className="space-y-4" data-testid="reset-success">
      <div className="text-sm text-emerald-300 border-l-2 border-emerald-500 pl-3 py-2 bg-emerald-500/5">
        Password updated. Other signed-in sessions were signed out.
      </div>
      <Link to="/login" data-testid="reset-go-login"
        className={`${authButtonClass} flex items-center justify-center`}>
        Sign In
      </Link>
    </div>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const tokenState = useTokenCheck(token);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_LEN) return setError(`Password must be at least ${MIN_LEN} characters.`);
    if (password !== confirm) return setError("Passwords don't match.");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const body = done ? <ResetDone />
    : tokenState === "checking" ? <div className="text-sm text-slate-400" data-testid="reset-checking">Checking link…</div>
    : tokenState === "invalid" ? <InvalidLink />
    : (
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <div>
          <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">New Password</Label>
          <Input data-testid="reset-password" type="password" value={password} required autoFocus
            onChange={(e) => setPassword(e.target.value)} className={authInputClass} />
        </div>
        <div>
          <Label className="text-[10px] font-condensed tracking-[0.3em] uppercase text-slate-400">Confirm Password</Label>
          <Input data-testid="reset-password-confirm" type="password" value={confirm} required
            onChange={(e) => setConfirm(e.target.value)} className={authInputClass} />
        </div>
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">At least {MIN_LEN} characters.</div>
        <AuthError testid="reset-error">{error}</AuthError>
        <Button type="submit" disabled={busy} data-testid="reset-submit" className={authButtonClass}>
          {busy ? "Updating…" : "Set New Password"}
        </Button>
      </form>
    );

  return (
    <AuthShell eyebrow="Account recovery" title="New Password" subtitle="Choose a new password for your staff account.">
      {body}
    </AuthShell>
  );
}
